/**
 * This is the type definition for the submitFormHack as-is, so that I can re-use it in the replacement function.
 */
type SubmitFormHackProps = {
  /** The URL to submit the form to */
  action?: string;
  /** The HTTP method to use for the form. */
  method: string;
  /**
   * The payload for the endpoint.
   *
   * We need to disable the no-explicit-any rule since this is the existing signature that which we want to match as
   * closely as possible, meaning we need to keep it as-is with the `any` type.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload?: { [key: string]: any };
  /** An array of keys that should not be sanitized. For example a password. */
  skipSanitizationKeys?: string[];
};

export default function submitFormHack({
  method,
  action = '',
  payload,
  skipSanitizationKeys = [],
}: SubmitFormHackProps) {
  const form = document.createElement('form');

  form.style.display = 'none';
  form.method = method;
  form.action = action;

  if (payload) {
    Object.entries(payload)
      .filter(([, value]) => value != null)
      .forEach(([name, value]) => {
        const input = document.createElement('input');
        input.name = sanitizeInput(name);
        input.value = skipSanitizationKeys.includes(name)
          ? String(value)
          : sanitizeInput(String(value));
        form.appendChild(input);
      });
  }

  document.body.appendChild(form);
  form.submit();
}

/**
 * This function takes an input string, assigns it to the textContent property of a newly created div,
 * and then returns the sanitized HTML content of the div. Since textContent does not parse HTML,
 * any harmful tags or scripts in the input will be treated as plain text, not as code.
 *
 * i.e.:
 * sanitizeInput("<script>window.alert('hi')</script>") -> "&lt;script&gt;window.alert('hi')&lt;/script&gt;"
 *
 * https://asecurityteam.atlassian.net/browse/VULN-1229071
 */
function sanitizeInput(input: string) {
  const div = document.createElement('div');
  div.textContent = input;
  return div.innerHTML;
}

type SubmitFormHackReplacementProps = SubmitFormHackProps & {
  /** The HTTP method to use. We're narrowing to only POST because the function we're replacing was only ever called with POST as the method. */
  method: 'POST';
  /** The CSRF token for the request, this is required here only because the previous form didn't require this. */
  csrfToken?: string;
  /** Enables passing the same-origin cookies with the request. Used for things like the CSRF token. Defaults to true */
  enablePassingCookies?: boolean;
};

/**
 * This function is a replacement for the submitFormHack function. It uses fetch rather than creating a form and submitting it.
 *
 * This version includes headers which weren't required before. Headers:
 * - Content-Type: application/json
 * - X-CSRF-TOKEN: csrfToken (if provided, not all routes need it [e.g. for id-auth, see: KIRBY-7781])
 *
 * It ensures all keys and values are sent as strings, including non-string primitives, this is purely for consistency with
 * the function that it is replacing.
 */
export function submitFormHackReplacement({
  method = 'POST',
  action = '',
  payload,
  csrfToken,
  enablePassingCookies = false,
}: SubmitFormHackReplacementProps): Promise<Response> {
  const requestPromise = fetch(action, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(csrfToken ? { 'X-CSRF-TOKEN': csrfToken } : {}),
      ...(enablePassingCookies ? { credentials: 'same-origin' } : {}),
    },
    body: JSON.stringify(payload),
  });

  return requestPromise;
}
